import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiVerificationDefaultCandidateCriteria,
  AiVerificationDistribution,
  AiVerificationQuestion,
  AiVerificationRandomConstraints,
  AiVerificationTemplate,
  DistrictMode,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * AI 検証テンプレ管理 hook (admin 第 23 弾、Phase 5 A-S19 / A-S20)。
 *
 * 旧 `old/apps/admin/src/features/verification-admin/lib/templates.ts` 相当を v2 で onSnapshot に
 * リアクティブ化しつつ、create / update / setStatus の 3 関数で minimum scope を提供
 * (delete / duplicate / master 連携 / AssignmentSet UI は別 sprint = A-S18 / A-S21〜23 / GAP)。
 *
 * 認可: firestore.rules `isDistrictAdmin(districtId)` → master / district admin (担当地区) のみ
 * write 可能。物理 delete は rules で禁止 (archivedAt + status='archived' 論理削除運用)。
 *
 * 旧との差分:
 *   - getDocs (one-shot) → onSnapshot (reactive)
 *   - create / update を分離 (旧は saveTemplate で全置換)
 *   - master 連携 (linked / forked) は forward-compatible に schema 保持のみ、操作 UI は別 sprint
 */

export type VerificationTemplatesState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: VerificationTemplateListItem[]; error: null }
  | { status: 'error'; items: []; error: Error };

/** 一覧表示用 narrow 型。AiVerificationTemplate をそのまま使うと FieldValue が混じる risk があるので map で正規化。 */
export type VerificationTemplateListItem = AiVerificationTemplate;

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asStatus(value: unknown): AiVerificationTemplate['status'] {
  return value === 'published' || value === 'archived' ? value : 'draft';
}

/**
 * targetMode の読込 narrow (MD-3 follow-up、モードドリブン化 2026-07-24)。
 * 旧実装は組込4種を whitelist し、編集画面 (書込側) の whitelist 撤廃後も読込側で custom mode
 * の targetMode を null に潰していた (保存→再読込で消えるデータ欠落バグ)。書込側と対称に
 * 非空 string をそのまま受理する。
 */
function asTargetMode(value: unknown): DistrictMode | null {
  if (typeof value === 'string' && value.trim()) return value.trim() as DistrictMode;
  return null;
}

function asQuestions(value: unknown): AiVerificationQuestion[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (q): q is AiVerificationQuestion =>
      typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
  );
}

function asDistribution(value: unknown): AiVerificationDistribution | undefined {
  if (
    value === 'all_same' ||
    value === 'random_per_user' ||
    value === 'manual' ||
    value === 'free'
  ) {
    return value;
  }
  return undefined;
}

function asRandomConstraints(value: unknown): AiVerificationRandomConstraints | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.minPostsPerUser !== 'number') return undefined;
  return {
    minPostsPerUser: v.minPostsPerUser,
    minOwnPosts: typeof v.minOwnPosts === 'number' ? v.minOwnPosts : 0,
    allowDuplicates: typeof v.allowDuplicates === 'boolean' ? v.allowDuplicates : false,
    preferOwnPosts: typeof v.preferOwnPosts === 'boolean' ? v.preferOwnPosts : true,
    preferLessVerified: typeof v.preferLessVerified === 'boolean' ? v.preferLessVerified : false,
  };
}

function asCandidateCriteria(value: unknown): AiVerificationDefaultCandidateCriteria | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (v.type !== 'all_ai_done' && v.type !== 'session_only' && v.type !== 'manual_pick') {
    return undefined;
  }
  const out: AiVerificationDefaultCandidateCriteria = { type: v.type };
  if (v.sessionScope === 'set_session' || v.sessionScope === 'all_sessions') {
    out.sessionScope = v.sessionScope;
  }
  return out;
}

/**
 * Firestore doc → AiVerificationTemplate。
 * 不正値は default fallback、optional フィールドは presence-aware に保持。
 */
export function mapVerificationTemplate(snap: {
  id: string;
  data: () => DocumentData;
}): VerificationTemplateListItem {
  const data = snap.data();
  const out: VerificationTemplateListItem = {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    description: typeof data.description === 'string' ? data.description : '',
    targetMode: asTargetMode(data.targetMode),
    perPostQuestions: asQuestions(data.perPostQuestions),
    perClassificationQuestions: asQuestions(data.perClassificationQuestions),
    allowFreeComment: typeof data.allowFreeComment === 'boolean' ? data.allowFreeComment : true,
    status: asStatus(data.status),
    version: typeof data.version === 'number' ? data.version : 0,
    createdAt: tsToIso(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: tsToIso(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
  const dist = asDistribution(data.defaultDistribution);
  if (dist) out.defaultDistribution = dist;
  const rc = asRandomConstraints(data.defaultRandomConstraints);
  if (rc) out.defaultRandomConstraints = rc;
  const cc = asCandidateCriteria(data.defaultCandidateCriteria);
  if (cc) out.defaultCandidateCriteria = cc;
  if (typeof data.defaultEndAtOffsetDays === 'number') {
    out.defaultEndAtOffsetDays = data.defaultEndAtOffsetDays;
  }
  if (data.sourceMasterTemplateId !== undefined) {
    out.sourceMasterTemplateId =
      typeof data.sourceMasterTemplateId === 'string' ? data.sourceMasterTemplateId : null;
  }
  if (data.linkedToMaster !== undefined) {
    const v = data.linkedToMaster;
    if (v === 'linked' || v === 'forked' || v === 'local') out.linkedToMaster = v;
  }
  if (typeof data.masterVersion === 'number') out.masterVersion = data.masterVersion;
  // GAP-A12 (Sprint BE) — classificationId は AiVerificationTemplate 型に optional 追加。
  // presence-aware に保持 (null も値として尊重)。
  if (data.classificationId !== undefined) {
    const v = data.classificationId;
    if (typeof v === 'string') {
      (
        out as VerificationTemplateListItem & { classificationId?: string | null }
      ).classificationId = v;
    } else if (v === null) {
      (
        out as VerificationTemplateListItem & { classificationId?: string | null }
      ).classificationId = null;
    }
  }
  return out;
}

export function useVerificationTemplates(districtId: string | null): VerificationTemplatesState {
  const [state, setState] = useState<VerificationTemplatesState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'aiVerificationTemplates'),
      orderBy('updatedAt', 'desc'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapVerificationTemplate);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

/**
 * 新規テンプレ作成 (auto-ID)。返却は新規 ID。
 * 初期値は旧 createDefault 相当 (displayName='新しい検証テンプレ' / status='draft' / 空質問群)。
 */
export async function createVerificationTemplate(
  districtId: string,
  actorUid: string,
): Promise<string> {
  const ref = collection(db, 'districts', districtId, 'aiVerificationTemplates');
  const created = await addDoc(ref, {
    displayName: '新しい検証テンプレ',
    description: '',
    targetMode: null,
    perPostQuestions: [],
    perClassificationQuestions: [],
    allowFreeComment: true,
    status: 'draft',
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/** 編集 form の patch 入力 (admin が触れる field のみ)。 */
export interface UpdateVerificationTemplateInput {
  displayName?: string;
  description?: string;
  targetMode?: DistrictMode | null;
  perPostQuestions?: AiVerificationQuestion[];
  perClassificationQuestions?: AiVerificationQuestion[];
  allowFreeComment?: boolean;
  status?: AiVerificationTemplate['status'];
  defaultDistribution?: AiVerificationDistribution;
  defaultRandomConstraints?: AiVerificationRandomConstraints;
  defaultCandidateCriteria?: AiVerificationDefaultCandidateCriteria;
  defaultEndAtOffsetDays?: number;
  /** GAP-A12 (Sprint BE) — M2 aiCategoryMasters への参照 (どの分類軸に対する検証か)。null = 全分類共通。 */
  classificationId?: string | null;
}

/**
 * 検証テンプレ複製 (GAP-A11、Sprint BE)。
 *
 * - displayName は元の名前に '(コピー)' を追記
 * - status は 'draft' に強制
 * - perPostQuestions / perClassificationQuestions / allowFreeComment / targetMode は引き継ぐ
 * - defaultDistribution / defaultRandomConstraints / defaultCandidateCriteria / defaultEndAtOffsetDays
 *   / classificationId も引き継ぐ
 * - master 連携メタは引き継がない (新規 local 扱い)
 */
export async function duplicateVerificationTemplate(
  districtId: string,
  source: VerificationTemplateListItem,
  actorUid: string,
): Promise<string> {
  const ref = collection(db, 'districts', districtId, 'aiVerificationTemplates');
  const payload: Record<string, unknown> = {
    displayName: `${source.displayName || '(無題)'}(コピー)`,
    description: source.description,
    targetMode: source.targetMode,
    perPostQuestions: source.perPostQuestions,
    perClassificationQuestions: source.perClassificationQuestions,
    allowFreeComment: source.allowFreeComment,
    status: 'draft' as AiVerificationTemplate['status'],
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  };
  if (source.defaultDistribution !== undefined) {
    payload.defaultDistribution = source.defaultDistribution;
  }
  if (source.defaultRandomConstraints !== undefined) {
    payload.defaultRandomConstraints = source.defaultRandomConstraints;
  }
  if (source.defaultCandidateCriteria !== undefined) {
    payload.defaultCandidateCriteria = source.defaultCandidateCriteria;
  }
  if (typeof source.defaultEndAtOffsetDays === 'number') {
    payload.defaultEndAtOffsetDays = source.defaultEndAtOffsetDays;
  }
  // classificationId は AiVerificationTemplate に optional に追加された (GAP-A12)、source object に
  // 存在すれば引き継ぐ。type 上 ((source as { classificationId?: string | null }).classificationId) で narrow。
  const sourceWithCls = source as VerificationTemplateListItem & {
    classificationId?: string | null;
  };
  if (sourceWithCls.classificationId !== undefined) {
    payload.classificationId = sourceWithCls.classificationId;
  }
  const created = await addDoc(ref, payload);
  return created.id;
}

/**
 * patch を merge:true で書込。version は +1、updatedAt/updatedBy を server で上書き。
 * undefined フィールドは Firestore SDK が無視するので safe (旧 stripUndefined 相当)。
 */
export async function updateVerificationTemplate(
  districtId: string,
  templateId: string,
  patch: UpdateVerificationTemplateInput,
  actorUid: string,
  currentVersion: number,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationTemplates', templateId);
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
    version: currentVersion + 1,
  };
  if (patch.displayName !== undefined) payload.displayName = patch.displayName;
  if (patch.description !== undefined) payload.description = patch.description;
  if (patch.targetMode !== undefined) payload.targetMode = patch.targetMode;
  if (patch.perPostQuestions !== undefined) payload.perPostQuestions = patch.perPostQuestions;
  if (patch.perClassificationQuestions !== undefined) {
    payload.perClassificationQuestions = patch.perClassificationQuestions;
  }
  if (patch.allowFreeComment !== undefined) payload.allowFreeComment = patch.allowFreeComment;
  if (patch.status !== undefined) payload.status = patch.status;
  if (patch.defaultDistribution !== undefined) {
    payload.defaultDistribution = patch.defaultDistribution;
  }
  if (patch.defaultRandomConstraints !== undefined) {
    payload.defaultRandomConstraints = patch.defaultRandomConstraints;
  }
  if (patch.defaultCandidateCriteria !== undefined) {
    payload.defaultCandidateCriteria = patch.defaultCandidateCriteria;
  }
  if (patch.defaultEndAtOffsetDays !== undefined) {
    payload.defaultEndAtOffsetDays = patch.defaultEndAtOffsetDays;
  }
  if (patch.classificationId !== undefined) {
    payload.classificationId = patch.classificationId;
  }
  await setDoc(ref, payload, { merge: true });
}

/**
 * status のみ変更 (一覧画面の quick toggle 用)。
 * version は +1、updatedAt/updatedBy を server で上書き。
 */
export async function setVerificationTemplateStatus(
  districtId: string,
  templateId: string,
  status: AiVerificationTemplate['status'],
  actorUid: string,
  currentVersion: number,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationTemplates', templateId);
  await setDoc(
    ref,
    {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
      version: currentVersion + 1,
    },
    { merge: true },
  );
}
